import { useCallback, useEffect, useState } from "react";
import { Form } from "antd";
import { ArrowLeft, ShieldAlert } from "lucide-react";
import AppInput from "../../../components/ui/AppInput";
import AppButton from "../../../components/ui/AppButton";
import Request from "../../../helpers/request.service";
import Notification from "../../../helpers/Notification";
import { authUrl } from "../../../constants/apiBaseUrl";

/**
 * LoginOtp — additive username + email-OTP login (single-factor, no password).
 *
 * Two steps: enter username → "Send code" (POST /auth/login/otp/start, always
 * 200) → enter the 6-digit code → "Verify" (POST /auth/login/otp/verify). On a
 * valid code the parent's finishLogin(payload) runs the identical post-login
 * routing as password login. "Send code" doubles as resend, gated by a
 * 60-second client-side countdown (the server rate-limits silently too).
 */
const RESEND_COOLDOWN_SECONDS = 60;

export default function LoginOtp({ onLoggedIn }) {
  const [step, setStep] = useState("username"); // "username" | "code"
  const [username, setUsername] = useState("");
  const [otp, setOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [deviceBlocked, setDeviceBlocked] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const sendCode = useCallback(async () => {
    const u = username.trim();
    if (!u) {
      setError("Username is required.");
      return;
    }
    setSending(true);
    setError(null);
    setDeviceBlocked(false);
    try {
      await Request.postJson(authUrl.login_otp_start, { username: u });
      // Anti-enumeration: always advances regardless of whether the user exists.
      setStep("code");
      setCooldown(RESEND_COOLDOWN_SECONDS);
      Notification.success("If the account exists, a login code has been emailed.");
    } catch (err) {
      const data = err?.response?.data;
      const message = data?.message || "Could not send the code. Please try again.";
      // Device-Bound Access (mTLS) WHERE-gate block → 403 with deviceBlocked.
      // Render the inline cert panel instead of the generic failure toast.
      const blocked = data?.data?.deviceBlocked === true;
      setDeviceBlocked(blocked);
      setError(message);
      if (!blocked) Notification.error(message);
    } finally {
      setSending(false);
    }
  }, [username]);

  const verifyCode = useCallback(async () => {
    const code = otp.trim();
    if (!code) {
      setError("Enter the code emailed to you.");
      return;
    }
    setVerifying(true);
    setError(null);
    try {
      const res = await Request.postJson(authUrl.login_otp_verify, {
        username: username.trim(),
        otp: code,
      });
      await onLoggedIn(res?.data?.data);
    } catch (err) {
      const message = err?.response?.data?.message || "Invalid or expired code.";
      setError(message);
      Notification.error(message);
    } finally {
      setVerifying(false);
    }
  }, [otp, username, onLoggedIn]);

  const backToUsername = () => {
    setStep("username");
    setOtp("");
    setError(null);
    setDeviceBlocked(false);
    setCooldown(0);
  };

  return (
    <Form layout="vertical" requiredMark={false} className="mt-8 space-y-4">
      {step === "username" ? (
        <>
          <AppInput
            id="otp-username"
            name="username"
            type="text"
            label="Username"
            required
            size="large"
            autoComplete="username"
            placeholder="Enter username"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              if (error) setError(null);
              if (deviceBlocked) setDeviceBlocked(false);
            }}
            onPressEnter={sendCode}
            status={error && !deviceBlocked ? "error" : undefined}
            error={deviceBlocked ? undefined : error || undefined}
          />
          {deviceBlocked ? (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                {error ||
                  "This machine is not registered for store access. Please contact your store manager to set it up."}
              </span>
            </div>
          ) : null}
          <AppButton
            type="primary"
            size="large"
            block
            loading={sending}
            disabled={sending || !username.trim()}
            onClick={sendCode}
            label="Send code"
          />
        </>
      ) : (
        <>
          <div className="text-sm text-mahogany">
            We&apos;ve emailed a 6-digit code to the account&apos;s registered address. Enter it
            below.
          </div>
          <AppInput
            id="otp-code"
            name="otp"
            type="text"
            inputMode="numeric"
            label="Login code"
            required
            size="large"
            autoComplete="one-time-code"
            placeholder="6-digit code"
            maxLength={6}
            value={otp}
            onChange={(e) => {
              setOtp(e.target.value.replace(/\D/g, ""));
              if (error) setError(null);
            }}
            onPressEnter={verifyCode}
            status={error ? "error" : undefined}
            error={error || undefined}
          />
          <AppButton
            type="primary"
            size="large"
            block
            loading={verifying}
            disabled={verifying || otp.trim().length < 6}
            onClick={verifyCode}
            label="Verify & sign in"
          />
          <div className="flex items-center justify-center">
            <AppButton
              type="link"
              size="small"
              loading={sending}
              disabled={sending || cooldown > 0}
              onClick={sendCode}
              className="!bg-transparent"
              label={cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
            />
          </div>
          <AppButton
            type="text"
            block
            onClick={backToUsername}
            className="!bg-transparent text-mahogany hover:text-cocoa"
            label={
              <span className="inline-flex items-center gap-1">
                <ArrowLeft className="h-4 w-4" /> Use a different username
              </span>
            }
          />
        </>
      )}
    </Form>
  );
}
